import React, { useState, useEffect, useRef } from 'react';
import { FileText, Printer, X, Download } from 'lucide-react';
import api from '../../api/client';
import { downloadPdf } from '../../utils/pdfUtils';

const ContratoUnicoButton = ({ lote, organizador, onClose }) => {
  const [showModal, setShowModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const iframeRef = useRef();

  const isModal = !!onClose;
  const isButton = !onClose;

  const modalOpen = isModal || showModal;

  useEffect(() => {
    if (modalOpen && organizador?.id && lote?.id) {
      loadPdf();
    }
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [modalOpen]);

  const loadPdf = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/organizadores/${organizador.id}/contratos/lotes/${lote.id}/contrato-unico`,
        { responseType: 'blob', timeout: 30000 }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      if (error.response?.status === 401) return;
      console.error('Erro ao carregar PDF:', error);
      alert('Erro ao gerar PDF. Verifique se o backend está rodando.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!pdfUrl) return;
    await downloadPdf(pdfUrl, `ContratoUnico-Lote-${lote.numero_lote}.pdf`);
  };

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };

  const fecharModal = () => {
    setShowModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    if (onClose) onClose();
  };

  if (isButton && !showModal) {
    return (
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
        title="Contrato Único"
      >
        <FileText size={16} />
        Contrato Único
      </button>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Gerando contrato...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl" style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <FileText size={24} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Contrato Único</h2>
              <p className="text-sm text-gray-600">Lote {lote.numero_lote}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={!pdfUrl}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Download size={16} />
              Baixar PDF
            </button>
            <button
              onClick={handlePrint}
              disabled={!pdfUrl}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Printer size={16} />
              Imprimir
            </button>
            <button
              onClick={fecharModal}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* PDF iframe */}
        <div className="flex-1 overflow-hidden rounded-b-xl">
          {pdfUrl ? (
            <iframe
              ref={iframeRef}
              src={pdfUrl}
              title="Contrato Único PDF"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              PDF não disponível
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContratoUnicoButton;
